import React, { useRef, useState } from 'react';

import { Close } from '@videoblocks/react-icons';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { useVideoUI } from './context/VideoEditorContext';

const ReferenceImageUpload: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const { referenceImage, handleReferenceImageChange } = useVideoUI();

  const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB in bytes

  const truncateFilename = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return filename.length > 10 ? `${filename.slice(0, 10)}...` : filename;
    }

    const name = filename.slice(0, lastDotIndex);
    const extension = filename.slice(lastDotIndex);

    if (name.length > 10) {
      return `${name.slice(0, 10)}...${extension}`;
    }

    return filename;
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (16MB limit)
    if (file.size > MAX_FILE_SIZE) {
      setError('Image size must be less than 16MB');
      return;
    }

    // Convert to data URI
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUri = event.target?.result as string;
      if (dataUri) {
        setSelectedFileName(file.name);
        handleReferenceImageChange(dataUri);
      }
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    handleReferenceImageChange(null);
    setSelectedFileName(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {!referenceImage && (
        <button
          type="button"
          onClick={handleFileSelect}
          className="flex items-center gap-1"
          aria-label="Upload reference image"
        >
          <img
            src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/ai-image-upload.svg`}
            alt="Upload reference"
            className="w-6 h-6"
          />
          <Text
            fontSize="sm"
            className="text-gray-250 m-0"
            fontWeight="regular"
          >
            Attach reference image
          </Text>
        </button>
      )}

      {/* Reference image preview - shown when image is selected */}
      {referenceImage && (
        <div className="mt-2 flex items-center gap-2">
          <div className="relative">
            <img
              src={referenceImage || undefined}
              alt="Reference upload preview"
              className="w-8 h-8 object-cover rounded border border-gray-600"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
              aria-label="Remove reference image"
            >
              <Close className="w-2 h-2 fill-white" />
            </button>
          </div>
          <Text fontSize="xs" className="text-gray-300 m-0">
            {selectedFileName ? truncateFilename(selectedFileName) : 'image'}
          </Text>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-2">
          <Text fontSize="xs" className="text-red-400 m-0">
            {error}
          </Text>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg, image/jpg"
        onChange={handleFileChange}
        className="hidden"
        aria-label="Select reference image"
      />
    </div>
  );
};

export default ReferenceImageUpload;
